import axios, { AxiosResponse } from 'axios';

import { API_URL } from '.';

export interface DeviceNotification {
  id: string;
  alarmId: number;
  messageId: number;
  deviceId: string;
  datetime: number;
  createdDatetime: number;
  status: string;
  headerId: string;
  descriptionId: string;
  equipName: string;
  header: string;
  description: string;
  severity: number;
  type: string;
  action: string;
  parameterIds: any[];
  slaveId: number;
  _etag: string;
  _ts: number;
}

export enum EventType {
  Alarm = 'Alarm',
  NewFirmwareAvailable = 'NewFirmwareAvailable',
  DeviceOnline = 'DeviceOnline',
  DeviceOffline = 'DeviceOffline',
  Schedule = 'Schedule',
  Members = 'Members',
  Settings = 'Settings',
  Parameters = 'Parameters',
  Info = 'Info',
  Help = 'Help',
}

export interface EventLogItem {
  id: string;
  eventType: EventType;
  eventMessage: string;
  eventLogDateTime: string;
  isRead: boolean;
  // Alarm-specific fields (optional, only present for alarm events)
  alarmId?: string;
  alarmSeverity?: number;
  /**
   * Indicates if this alarm is currently active.
   *
   * What Happens When a New Alarm Occurs:
   * 1. Backend Logic: Only the newest/most critical alarm gets isActiveAlarm: true
   * 2. Previous Active Alarm: Automatically becomes isActiveAlarm: false on the backend
   * 3. Frontend Refetch: Gets the updated data with the new active alarm
   * 4. UI Update: The new alarm replaces the previous one in the "Active Alarm" section
   * 5. Previous Alarm: Moves to its chronological section (Today, Yesterday, etc.)
   */
  isActiveAlarm?: boolean;
  descriptionText?: string;
}

export interface EventLogResponse {
  page: EventLogItem[];
  totalCount: number;
}

/**
 * Event Log Category Filter Values
 *
 * These values are sent to the backend as the 'category' parameter.
 * Mapping to UI filter labels:
 * - Alarm → "Alarm" filter (shows EventType.Alarm with severity 1)
 * - Warning → "Warning" filter (shows EventType.Alarm with severity >= 2)
 * - Notification → "Firmware update" filter (shows EventType.NewFirmwareAvailable)
 * - Event → "Event" filter (shows other event types: Schedule, Members, Settings, etc.)
 */
export enum EventLogCategory {
  Alarm = 'Alarm',
  Notification = 'Notification',
  Warning = 'Warning',
  Event = 'Event',
}

export interface EventLogsParams {
  systemId: string;
  pageSize?: number;
  page?: number;
  category?: string; // Comma-separated list of event types: "Alarm,Event,Notification"
  startDate?: string;
  endDate?: string;
}

export const getEventLogs = ({
  systemId,
  page = 1,
  pageSize = 10,
  category,
  startDate,
  endDate,
}: EventLogsParams): Promise<AxiosResponse<EventLogResponse>> =>
  axios.get(`${API_URL}/v2/groups/${systemId}/eventlogs`, {
    params: {
      page,
      pageSize,
      ...(category && { category }),
      ...(startDate && { dateFrom: startDate }),
      ...(endDate && { dateTo: endDate }),
    },
  });

export const markEventLogsAsRead = (groupId: string) =>
  axios.put(`${API_URL}/v2/groups/${groupId}/eventlogs/readstate`);

export const getDeviceNotification = (
  deviceId: string,
  notificationId: string,
): Promise<AxiosResponse<DeviceNotification>> =>
  axios.get(`${API_URL}/v2/devices/${deviceId}/notifications/${notificationId}`);
